import { inject, Service } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { authenticationControllerGetProfileV1 } from '@lexplus-authentication-api';
import { API_CONFIGURATION } from '@lexplus-environments';
import {
  checkPasswordPolicy,
  notify,
  PasswordPolicyCheck,
  ProfileStateClear,
  ProfileStateSetIsLoggedIn,
  ProfileStateSetProfile,
} from '@lexplus-shared-lib';
import { Router } from '@angular/router';
import { SESSION_EXPIRED_TOAST_ID } from '../auth-notifications.const';

@Service()
export class AuthenticationService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);

  /**
   * Checks a password against the policy; delegates to the shared function, which is the single
   * place the rules live.
   */
  public checkPasswordPolicy(password: string): PasswordPolicyCheck {
    return checkPasswordPolicy(password);
  }

  /**
   * Routes onwards after a successful login depending on the password status and
   * loads the profile in the normal case.
   */
  public async routeAndProcessAfterLogin(
    mustChangePassword: boolean,
    isFirstLogin: boolean,
    passwordChangeToken: string | null,
    email: string,
  ): Promise<void> {
    // Check isFirstLogin first: for a new user both flags are true
    // (PasswortNeuSetzenAm = now, LetztesLoginAm = null). The first-login screen
    // (replace the initial password) then takes precedence over the regular reset screen.
    if (isFirstLogin) {
      await this.router.navigateByUrl(this.buildPasswordChangeUrl('/auth/initial-password', email, passwordChangeToken, 'initial'));
      return;
    }

    if (mustChangePassword) {
      await this.router.navigateByUrl(this.buildPasswordChangeUrl('/auth/reset-password', email, passwordChangeToken, 'forced'));
      return;
    }

    await this.loadProfileAndSetInState();

    await this.router.navigateByUrl('/dashboard');
  }

  /**
   * Loads the profile from the API (cookie-authenticated) and stores it in the state.
   * @throws The API call's error if the profile cannot be loaded.
   */
  public async loadProfileAndSetInState(): Promise<void> {
    try {
      const response = await lastValueFrom(authenticationControllerGetProfileV1(this.httpClient, this.apiConfiguration.baseUrlAuth));
      this.store.dispatch(new ProfileStateSetProfile(response.body));
      this.store.dispatch(new ProfileStateSetIsLoggedIn(true));
      // «You were logged out» stays until dismissed, which is right while the user is
      // away but wrong the moment they are back — so signing in retracts it. This is the
      // one place every sign-in path passes through.
      notify.dismiss(SESSION_EXPIRED_TOAST_ID);
    } catch (loadProfileException) {
      this.store.dispatch(new ProfileStateSetIsLoggedIn(false));
      this.store.dispatch(new ProfileStateClear());
      throw loadProfileException;
    }
  }

  /**
   * Builds a password-change URL with properly encoded query parameters.
   */
  private buildPasswordChangeUrl(
    path: string,
    email: string,
    passwordChangeToken: string | null,
    changetype: 'self' | 'forced' | 'initial' | undefined,
  ): string {
    const encodedEmail = encodeURIComponent(email);
    const encodedToken = encodeURIComponent(passwordChangeToken ?? '');
    return `${path}?email=${encodedEmail}&token=${encodedToken}&changetype=${changetype}`;
  }
}
